<template>
  <div>
    <h2 class="h2">Business Relationships</h2>

    <h3 class="h3">Default relationships</h3>
    <div class="manage-relationships__table">
      <table class="relationships-table">
        <thead>
        <tr>
          <th>Label</th>
          <th>Inverse Label</th>
          <th>Alias</th>
          <th>Inverse Alias</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="(relationship, index) in defaultBusinessRelationships"
            :key="'default_business_' + index"
            :class="{ 'editing' : editingRelationship.id === relationship.id, 'deleting': deletingRelationshipId === relationship.id }">
          <td>
              <span @click="copyToClipboard(relationship.name)"
                    v-tooltip.right="'Copy name to use in spreadsheets: ' + relationship.name"
                    class="copyable default-label">
                {{ relationship.label }} <icon name="copy" size="14"/>
              </span>
          </td>
          <td>
              <span @click="copyToClipboard(relationship.inverse_name)"
                    v-tooltip.right="'Copy name to use in spreadsheets: ' + relationship.inverse_name"
                    class="copyable default-label">
                {{ relationship.inverse_label }} <icon name="copy" size="14"/>
              </span>
          </td>

          <td v-if="editingRelationship.id !== relationship.id">
            <span v-if="relationship.alias" class="alias">{{ relationship.alias }}</span>
            <span v-else class="no-alias">—</span>
          </td>
          <td v-else>
            <input type="text" placeholder="alias (optional)" @keyup="handleEditKeyup"
                   v-model="editingRelationship.alias" class="table-input">
          </td>

          <td v-if="editingRelationship.id !== relationship.id">
            <span v-if="relationship.reverse_alias" class="alias">{{ relationship.reverse_alias }}</span>
            <span v-else class="no-alias">—</span>
          </td>
          <td v-else>
            <input type="text" placeholder="reverse alias (optional)" @keyup="handleEditKeyup"
                   v-model="editingRelationship.reverse_alias" class="table-input">
          </td>

          <td>
            <div class="action-buttons">
              <icon v-if="!editingRelationship.id && !deletingRelationshipId" class="icon"
                    name="edit" @click="setEditingRelationship(relationship)"/>
              <icon v-if="editingRelationship.id === relationship.id && !updatingRelationship"
                    class="icon" name="check" @click="updateRelationship"/>
              <icon v-if="(editingRelationship.id === relationship.id && updatingRelationship) || deletingRelationshipId === relationship.id"
                    class="icon" name="spinner"/>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <br/>
    <h3 class="h3">Custom relationships
      <icon name="plus" style="margin-bottom: -5px; cursor: pointer"
            @click="showAddMore = !showAddMore;focusTagInput()"></icon>
    </h3>
    <div class="subtitle" style="margin-top: 0.5rem">List of custom relationships</div>
    <div class="manage-relationships__table">
      <table class="relationships-table">
        <thead>
        <tr>
          <th>Label</th>
          <th>Inverse Label</th>
          <th>Alias</th>
          <th>Reverse Alias</th>
          <th>Actions</th>
        </tr>
        </thead>
        <tbody>
        <tr v-show="showAddMore" class="add-row">
          <td>
            <input placeholder="provides liquidity" type="text" v-model="label"
                   ref="addMore" @keyup="handleCreateKeyup" class="table-input">
          </td>
          <td>
            <input placeholder="receives liquidity" type="text" v-model="inverseLabel"
                   @keyup="handleCreateKeyup" class="table-input">
          </td>
          <td>
            <input placeholder="alias (optional)" type="text" v-model="alias"
                   @keyup="handleCreateKeyup" class="table-input">
          </td>
          <td>
            <input placeholder="reverse alias (optional)" type="text" v-model="reverseAlias"
                   @keyup="handleCreateKeyup" class="table-input">
          </td>
          <td>
            <div class="action-buttons">
              <icon v-if="label.length >= 2 && inverseLabel.length >= 2"
                    class="icon" name="check" @click="addRelationship"/>
            </div>
          </td>
        </tr>
        <tr v-for="(relationship, index) in customBusinessRelationships"
            :key="'business_' + index"
            :class="{ 'editing' : editingRelationship.id === relationship.id, 'deleting': deletingRelationshipId === relationship.id }">
          <td v-if="editingRelationship.id !== relationship.id">
              <span @click="copyToClipboard(relationship.name)"
                    v-tooltip.right="'Copy name to use in spreadsheets: ' + relationship.name"
                    class="copyable">
                {{ relationship.label }} <icon name="copy" size="14"/>
              </span>
          </td>
          <td v-else>
            <input type="text" v-model="editingRelationship.label"
                   @keyup="handleEditKeyup" :ref="'edit-custom' + relationship.id" class="table-input">
          </td>

          <td v-if="editingRelationship.id !== relationship.id">
              <span @click="copyToClipboard(relationship.inverse_name)"
                    v-tooltip.right="'Copy name to use in spreadsheets: ' + relationship.inverse_name"
                    class="copyable">
                {{ relationship.inverse_label }} <icon name="copy" size="14"/>
              </span>
          </td>
          <td v-else>
            <input type="text" @keyup="handleEditKeyup"
                   v-model="editingRelationship.inverse_label" class="table-input">
          </td>

          <td v-if="editingRelationship.id !== relationship.id">
            <span v-if="relationship.alias" class="alias">{{ relationship.alias }}</span>
            <span v-else class="no-alias">—</span>
          </td>
          <td v-else>
            <input type="text" placeholder="alias (optional)" @keyup="handleEditKeyup"
                   v-model="editingRelationship.alias" class="table-input">
          </td>

          <td v-if="editingRelationship.id !== relationship.id">
            <span v-if="relationship.reverse_alias" class="alias">{{ relationship.reverse_alias }}</span>
            <span v-else class="no-alias">—</span>
          </td>
          <td v-else>
            <input type="text" placeholder="Inverse alias (optional)" @keyup="handleEditKeyup"
                   v-model="editingRelationship.reverse_alias" class="table-input">
          </td>

          <td>
            <div class="action-buttons">
              <icon v-if="!editingRelationship.id && !deletingRelationshipId" class="icon"
                    name="edit" @click="setEditingRelationship(relationship)"/>
              <icon
                v-if="editingRelationship.id === relationship.id && !updatingRelationship && editingRelationship.inverse_label.length >= 2 && editingRelationship.label.length >= 2"
                class="icon" name="check" @click="updateRelationship"/>
              <icon v-if="(editingRelationship.id === relationship.id && updatingRelationship) || deletingRelationshipId === relationship.id"
                    class="icon" name="spinner"/>
              <icon v-if="!editingRelationship.id && !deletingRelationshipId" class="icon"
                    name="trash" @click="deleteRelationship(relationship)"/>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
  import AutocompleteTagInput from '../../Form/AutocompleteTagInput.vue'
  import TagsMixin from '../../../util/TagsMixin'
  import Dropdown from '../../Dropdown/Dropdown.vue'

  import { ACTION_TYPES as RELATIONSHIP_ACTION_TYPES } from '../../../store/modules/actor-relationships'
  import { countRelationships, createRelationship, deleteRelationship, updateRelationship, } from '../../../api/actor-relationships'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../../store/modules/ui.js'
  import MODAL_IDS from '../../../constants/modal-ids.js'

  import { copyToClipboard } from '../../../util/helpers.js'
  import { defineComponent } from 'vue'

  export default defineComponent({
    name: 'ManageBusinessRelationships',
    data () {
      return {
        errors: [],
        label: '',
        inverseLabel: '',
        alias: '',
        reverseAlias: '',
        editingRelationship: {
          label: '',
          inverse_label: '',
          alias: '',
          reverse_alias: '',
        },
        updatingRelationship: false,
        submitting: false,
        showAddMore: false,
        deletingRelationshipId: null,
      }
    },
    computed: {
      relationships () {
        return this.$store.state.actorRelationships.relationships
      },
      businessRelationships () {
        return this.relationships.filter(relationship => relationship.type === 'business') || []
      },
      defaultBusinessRelationships () {
        return this.businessRelationships.filter(rel => rel.default)
      },
      customBusinessRelationships () {
        return this.businessRelationships.filter(rel => !rel.default).reverse()
      },
    },
    methods: {
      copyToClipboard,
      focusEditCustomRelationship (relationship) {
        setTimeout(() => {
          const ref = 'edit-custom' + relationship.id
          if (this.$refs[ref] && this.$refs[ref][0]) {
            this.$refs[ref][0].focus()
          }
        })
      },
      handleCreateKeyup ($event) {
        if (!this.label || !this.inverseLabel) {
          return
        }
        if ($event.keyCode === 13) {
          this.addRelationship()
        }
      },
      handleEditKeyup ($event) {
        if (!this.editingRelationship.label.length >= 2 || !this.editingRelationship.id || !this.editingRelationship.inverse_label.length >= 2) {
          return
        }
        if ($event.keyCode === 13) {
          this.updateRelationship()
        }
      },
      setEditingRelationship (relationship) {
        this.editingRelationship = { ...relationship }
        this.focusEditCustomRelationship(relationship)
      },
      updateRelationship () {
        if (!this.editingRelationship || !this.editingRelationship.id) {
          return
        }
        this.updatingRelationship = true

        updateRelationship(this.editingRelationship.id, {
          label: this.editingRelationship.label,
          inverseLabel: this.editingRelationship.inverse_label,
          alias: this.editingRelationship.alias,
          reverseAlias: this.editingRelationship.reverse_alias,
        })
          .then(response => {
            this.$store.dispatch(RELATIONSHIP_ACTION_TYPES.FETCH_RELATIONSHIPS)
          })
          .catch(error => {
            console.log(error)
          })
          .finally(() => {
            this.updatingRelationship = false
            this.editingRelationship = {
              label: '',
              inverse_label: '',
              alias: '',
              reverse_alias: '',
            }
          })
      },
      focusTagInput () {
        setTimeout(() => {
          this.$refs.addMore.focus()
        })
      },
      addRelationship () {
        this.submitting = true
        createRelationship({
          label: this.label,
          inverseLabel: this.inverseLabel,
          alias: this.alias,
          reverseAlias: this.reverseAlias
        })
          .then(response => {
          })
          .catch(error => {
            console.log(error)
            this.$store.dispatch(RELATIONSHIP_ACTION_TYPES.FETCH_RELATIONSHIPS)
          })
          .finally(() => {
            this.$store.dispatch(RELATIONSHIP_ACTION_TYPES.FETCH_RELATIONSHIPS)
            this.submitting = false
            this.showAddMore = false
            this.label = ''
            this.inverseLabel = ''
            this.alias = ''
            this.reverseAlias = ''
          })
      },
      deleteRelationship (relationship) {
        countRelationships(relationship.id).then(result => {
          if (result.relationship_count === 0) {
            this.confirmDeleteCustomRelationship(relationship.id)
          } else {
            this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
              name: relationship.label + ' / ' + relationship.inverse_label,
              countableEntityName: 'relationship',
              count: result.relationship_count,
              resource: relationship,
              modalContextType: 'countable-entity',
            })

            this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
          }
        })
      },
      confirmDeleteCustomRelationship (relationshipId) {
        this.deletingRelationshipId = relationshipId
        deleteRelationship(relationshipId)
          .then(response => {
            this.$store.dispatch(RELATIONSHIP_ACTION_TYPES.FETCH_RELATIONSHIPS)
          })
          .catch(error => {
            console.log(error)
            this.$store.dispatch(RELATIONSHIP_ACTION_TYPES.FETCH_RELATIONSHIPS)
          })
          .finally(() => {
            this.deletingRelationshipId = null
          })
      },
    },
    mounted () {
      this.$bus.on('confirmDeleteCountableEntity', (context) => {
        this.confirmDeleteCustomRelationship(context.resource.id)
      })
    },
    beforeUnmount () {
      this.$bus.off('confirmDeleteCountableEntity')
    },
    mixins: [TagsMixin],
    components: {
      AutocompleteTagInput,
      Dropdown,
    },
  })
</script>

<style lang="scss" scoped>
  .manage-relationships__table {
    margin: 1rem 0;
  }

  .relationships-table {
    width: 100%;
    border-collapse: collapse;
    background: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    th {
      background: #f8f9fa;
      padding: 12px 16px;
      text-align: left;
      font-weight: 600;
      color: #495057;
      border-bottom: 2px solid #dee2e6;

      &:first-child {
        width: 22%;
      }

      &:nth-child(2) {
        width: 22%;
      }

      &:nth-child(3) {
        width: 18%;
      }

      &:nth-child(4) {
        width: 18%;
      }

      &:last-child {
        width: 20%;
        text-align: center;
      }
    }

    td {
      padding: 12px 16px;
      border-bottom: 1px solid #dee2e6;
      vertical-align: middle;

      &:last-child {
        text-align: center;
      }
    }

    tbody tr {
      &:hover {
        background-color: #f8f9fa;
      }

      &.editing {
        background-color: #e3f2fd;
      }

      &.deleting {
        background-color: #ffebee;
      }

      &.add-row {
        background-color: #e8f5e8;

        &:hover {
          background-color: #e8f5e8;
        }
      }
    }
  }

  .table-input {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: #80bdff;
      box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }
  }

  .copyable {
    cursor: pointer;

    &:hover {
      color: #007bff;
    }
  }

  .alias {
    font-style: italic;
    color: #6c757d;
  }

  .no-alias {
    color: #adb5bd;
    font-size: 18px;
  }

  .default-label {
    color: #495057;
    font-weight: 500;

    &:hover {
      color: #007bff;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;

    .icon {
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: background-color 0.2s;

      &:hover {
        background-color: rgba(0, 0, 0, 0.1);
      }

      &[name="check"] {
        color: #28a745;
      }

      &[name="edit"] {
        color: #007bff;
      }

      &[name="trash"] {
        color: #dc3545;
      }

      &[name="spinner"] {
        color: #6c757d;
      }
    }
  }
</style>
